import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { StyledDiv, StyledLeadContainer, StyledIndexContainerDiv } from "../../Components/Styled";
import { handleValidateCodevalue } from "../../Utility/constant";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Stack, Alert } from "@mui/material";
import BiztantuLoader from "../../Components/Loader";
import SelectBoxWithLabel from "../../Components/SelectBoxWithLable";
import { ChartFilter } from "../../Components/ChartFilter";
import { searchRequestData, getRequestData, dateFormatting } from "../../Utility/apiHelper";
import ClientActivityChart from "./ClientActivityChart";
import { requestHideLoader, requestShowLoader } from "../../Store/Global/action";
import LeadAccordion from "../../Components/LeadAccordion";

// ─────────────────────────────────────────────────────────────────────────────

const BiztantuDashboard = () => {

    const internationalise = useSelector(state => state?.internationalise?.currentHashMapData);
    const isLoading = useSelector(state => state.global.isLoading);

    // Client dropdown options (fetched from API)
    const [clientOptions, setClientOptions] = useState([]);

    // Consolidated filter state
    const [dashboardActivity, setDashboardActivity] = useState({
        clientId: "",
        daysRange: 7,
        createdAt : "",
        fromDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
        toDate: moment().format("YYYY-MM-DD"),
    });
    const dispatch = useDispatch();
    const [clientData, setClientData] = useState([
    {
        "leadCount": 0,
        "customerCount": 0,
        "productCount": 0,
        "taskCount": 0
    }
]);

    // Fetch client list on mount
    const fetchClients = useCallback(async () => {
        dispatch(requestShowLoader())
        try {
            const response = await getRequestData(`${process.env.REACT_APP_BASE_URL}companies?status=CLIENT_STATUS_ACTIVE`);
            const companies = response?.data || [];

            const mapped = (companies || []).map((c) => ({
                id: c.id,
                name: c.name,
                createdAt : c.createdAt
            }));

            setClientOptions(mapped);
            const biztantuClient = mapped?.length
                ? mapped.reduce((lowest, current) =>
                    current.id < lowest.id ? current : lowest
                )
            : null;
            // default-select the first client once options arrive
            if (mapped?.length > 0) {
                setDashboardActivity((prev) => ({ ...prev, clientId: biztantuClient?.id }));
            }
        } catch (err) {
            console.error(err);
        }
        finally{
            dispatch(requestHideLoader())
        }
    }, []);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    const { clientId, daysRange, fromDate, toDate } = dashboardActivity;

    // Fetch activity whenever client/dates change
    const fetchActivity = useCallback(async () => {
        if (!clientId || !toDate) return;

        const queryParams = {
            params: { clientId, fromDate, toDate },
        };
        dispatch(requestShowLoader());
        try {
            const response = await searchRequestData(
                queryParams,
                `${process.env.REACT_APP_BASE_URL}dashboard/clients/activity`
            );
            setClientData(response?.data);
        } catch (err) {
            console.error(err);
        }
        finally{
            dispatch(requestHideLoader())
        }
    }, [clientId, fromDate, toDate]);

    useEffect(() => {
        fetchActivity();
    }, [fetchActivity]);

    const computeDateRange = (daysRange, clientId) => {
        const today = moment();

        const currentClient = clientOptions?.find(
            item => item?.id === clientId
        );

        const fromDate =
            daysRange === "TILL_NOW"
                ? currentClient?.createdAt
                    ? dateFormatting(currentClient.createdAt)
                    : null
                : dateFormatting(today.clone().subtract(Number(daysRange), "days"));

        return {
            fromDate,
            toDate: dateFormatting(today),
        };
    };

    const handleClientChange = (e) => {
        const clientId = e.target.value;

        setDashboardActivity((prev) => {
            const { fromDate, toDate } = computeDateRange(prev.daysRange, clientId);
            return {
                ...prev,
                clientId,
                fromDate,
                toDate,
            };
        });
    };

    const handleDaysRangeChange = (event) => {
        const daysRange = event.target.value;

        setDashboardActivity((prev) => {
            const { fromDate, toDate } = computeDateRange(daysRange, prev.clientId);
            return {
                ...prev,
                daysRange,
                fromDate,
                toDate,
            };
        });
    };

    return (
        <BiztantuLoader open={isLoading} isChildren={true}>
            <StyledDiv>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <StyledLeadContainer>
                        <Typography variant='h6' className='mb-20'>
                            {handleValidateCodevalue(internationalise, 'DASHBOARD_LABEL') || 'Dashboard'}
                        </Typography>
                    </StyledLeadContainer>
                </Box>

                {/* Scrollable content area */}
                <StyledIndexContainerDiv
                    className="global-dashboard hide-scrollbar"
                    >
                    {/* Filters: client + day-range */}
                     <LeadAccordion
                            label={handleValidateCodevalue(internationalise, 'DASH_CLIENT_DATA')}
                            defaultOpen = {true}
                        >
                    <Stack m={2} display="flex" flexWrap={"wrap"} flexDirection="row" alignItems="center" gap={3}>
                        <SelectBoxWithLabel
                            name="clientId"
                            label="Client"
                            isStatic={false}
                            ownerdropdown={true}
                            optionMenuItem={clientOptions}
                            value={clientId}
                            handleOnChange={handleClientChange}
                        />

                    </Stack>
                    <div className='piechartContainer'>
                        <ChartFilter
                            label={"Period"}
                            onChange={handleDaysRangeChange}
                            value={daysRange}
                            isType="Line"
                        />
                        <ClientActivityChart data={clientData} />
                    </div>
                    </LeadAccordion>
                </StyledIndexContainerDiv>
            </StyledDiv>
        </BiztantuLoader>
    );
};

export default BiztantuDashboard;